// Record your own audio onto a channel and edit the takes: move, trim, split,
// duplicate, gain, fades, normalize, nudge and delete. Clips are placed on the
// song's beat grid; the audio stays on this device (see Recordings.ts).
import {HTML} from "imperative-html/dist/esm/elements-strict.js";
import {AudioClip, recordedTakes} from "../synth/synth.js";
import {Config} from "../synth/SynthConfig.js";
import {SongDocument} from "./SongDocument.js";
import {Prompt} from "./Prompt.js";
import {ChangeChannelClips} from "./changes.js";
import {newTakeId, saveTake} from "./Recordings.js";

const {button, div, h2, label, input, select, option, canvas, p, span} = HTML;

const LANE_HEIGHT = 96;
const ZOOMS = [2, 4, 8, 16, 32, 64]; // timeline pixels per beat
const MAX_WIDTH = 16000; // stay under canvas size limits
const HANDLE = 7; // px at each clip edge that trims rather than moves
const MIN_SECONDS = 0.05;

type Drag = {mode: "move" | "start" | "end"; index: number; x: number; original: AudioClip};
type Take = {
	stream: MediaStream;
	source: MediaStreamAudioSourceNode;
	processor: ScriptProcessorNode;
	silent: GainNode;
	chunks: Float32Array[];
	firstCapture: number;
	startPart: number;
	prerollSeconds: number;
	playStart: number;
	metronome: boolean;
};

export class AudioTrackPrompt implements Prompt {
	private readonly _channel: number;
	private _clips: AudioClip[] = [];
	private readonly _original: AudioClip[];
	private readonly _undo: AudioClip[][] = [];
	private readonly _redo: AudioClip[][] = [];
	private _selected: number = -1;
	private _cursor: number = 0; // parts from the start of the song
	private _drag: Drag | null = null;
	private _take: Take | null = null;
	private _frame: number = 0;
	private _zoom: number = 3;

	private readonly _timeline: HTMLCanvasElement = canvas({class: "audio-timeline", tabindex: "0", height: LANE_HEIGHT + 22, "aria-label": "Recorded clips on this channel's timeline", role: "img"});
	private readonly _scroller: HTMLDivElement = div({class: "audio-scroll"}, this._timeline);
	private readonly _zoomOut: HTMLButtonElement = button({type: "button", "aria-label": "Zoom out", title: "Zoom out"}, "−");
	private readonly _zoomIn: HTMLButtonElement = button({type: "button", "aria-label": "Zoom in", title: "Zoom in"}, "+");
	private readonly _recordButton: HTMLButtonElement = button({type: "button", class: "audio-record"}, "● Record");
	private readonly _playButton: HTMLButtonElement = button({type: "button"}, "▶ Play from cursor");
	private readonly _stopButton: HTMLButtonElement = button({type: "button", disabled: true}, "■ Stop");
	private readonly _input: HTMLSelectElement = select({"aria-label": "Microphone"}, option({value: ""}, "Default microphone"));
	private readonly _countIn: HTMLInputElement = input({type: "checkbox", checked: true});
	private readonly _metronome: HTMLInputElement = input({type: "checkbox"});
	private readonly _replace: HTMLInputElement = input({type: "checkbox", checked: true});
	private readonly _status: HTMLParagraphElement = p({class: "audio-status", role: "status", "aria-live": "polite"});

	private readonly _gain: HTMLInputElement = input({type: "range", min: -24, max: 12, step: 0.5, "aria-label": "Clip gain"});
	private readonly _gainValue: HTMLSpanElement = span({class: "audio-value"});
	private readonly _fadeIn: HTMLInputElement = input({type: "number", min: 0, step: 10, "aria-label": "Fade in (ms)"});
	private readonly _fadeOut: HTMLInputElement = input({type: "number", min: 0, step: 10, "aria-label": "Fade out (ms)"});
	private readonly _splitButton: HTMLButtonElement = button({type: "button"}, "Split at cursor");
	private readonly _duplicateButton: HTMLButtonElement = button({type: "button"}, "Duplicate");
	private readonly _normalizeButton: HTMLButtonElement = button({type: "button"}, "Normalize");
	private readonly _earlierButton: HTMLButtonElement = button({type: "button", title: "Move 10 ms earlier"}, "◀ 10 ms");
	private readonly _laterButton: HTMLButtonElement = button({type: "button", title: "Move 10 ms later"}, "10 ms ▶");
	private readonly _deleteButton: HTMLButtonElement = button({type: "button", class: "audio-delete"}, "Delete clip");
	private readonly _clipControls: HTMLDivElement = div({class: "audio-clip-controls"},
		label("Gain ", this._gain, this._gainValue),
		label("Fade in ", this._fadeIn, " ms"),
		label("Fade out ", this._fadeOut, " ms"),
		div({class: "audio-clip-buttons"}, this._earlierButton, this._laterButton, this._splitButton, this._duplicateButton, this._normalizeButton, this._deleteButton),
	);
	private readonly _undoButton: HTMLButtonElement = button({type: "button", title: "Undo (Ctrl/⌘ Z)"}, "Undo");
	private readonly _redoButton: HTMLButtonElement = button({type: "button", title: "Redo (Ctrl/⌘ ⇧ Z)"}, "Redo");
	private readonly _cancelButton: HTMLButtonElement = button({class: "cancelButton"});
	private readonly _doneButton: HTMLButtonElement = button({class: "okayButton", type: "button"}, "Done");

	public readonly container: HTMLDivElement;

	constructor(private _doc: SongDocument) {
		this._channel = this._doc.channel;
		const channel = this._doc.song.channels[this._channel];
		this._original = channel.clips.map(clip => clip.clone());
		const name = channel.name || ((this._doc.song.getChannelIsNoise(this._channel) ? "Drum channel " : "Channel ") + (this._channel + 1));
		this._cursor = this._doc.bar * this._doc.song.beatsPerBar * Config.partsPerBeat;
		this.container = div({class: "prompt noSelection audio-track-prompt"},
			h2("Recorded audio · " + name),
			p({class: "audio-hint"}, "Record yourself while the song plays, then drag a clip to move it or its edges to trim. Click the timeline to set the cursor. Hold Shift for fine positioning. Recordings stay on this device; a shared link carries their placement only."),
			this._scroller,
			div({class: "audio-transport"}, this._recordButton, this._playButton, this._stopButton, this._input, span({class: "audio-zoom"}, this._zoomOut, this._zoomIn)),
			div({class: "audio-options"},
				label(this._countIn, " One bar of lead-in"),
				label(this._metronome, " Metronome"),
				label(this._replace, " Replace audio under a new take"),
			),
			this._status,
			this._clipControls,
			div({class: "audio-footer"}, this._undoButton, this._redoButton, this._doneButton),
			this._cancelButton,
		);

		this._refresh();
		this._doc.notifier.watch(this._whenSongChanged);
		this._timeline.addEventListener("pointerdown", this._whenPointerDown);
		this._timeline.addEventListener("pointermove", this._whenPointerMove);
		this._timeline.addEventListener("pointerup", this._whenPointerUp);
		this._timeline.addEventListener("pointercancel", this._whenPointerUp);
		this.container.addEventListener("keydown", this._whenKeyDown);
		this._recordButton.addEventListener("click", () => void this._startRecording());
		this._playButton.addEventListener("click", this._play);
		this._stopButton.addEventListener("click", () => void this._stop());
		this._cancelButton.addEventListener("click", this._cancel);
		this._doneButton.addEventListener("click", this._done);
		this._undoButton.addEventListener("click", this._undoEdit);
		this._redoButton.addEventListener("click", this._redoEdit);
		this._gain.addEventListener("input", () => { this._gainValue.textContent = this._gain.value + " dB"; });
		this._gain.addEventListener("change", () => this._edit(clip => { clip.gain = +this._gain.value; }));
		this._fadeIn.addEventListener("change", () => this._edit(clip => { clip.fadeIn = Math.max(0, Math.min(clip.duration, (+this._fadeIn.value || 0) / 1000)); }));
		this._fadeOut.addEventListener("change", () => this._edit(clip => { clip.fadeOut = Math.max(0, Math.min(clip.duration, (+this._fadeOut.value || 0) / 1000)); }));
		this._earlierButton.addEventListener("click", () => this._edit(clip => { clip.start = Math.max(0, clip.start - 0.01 / this._partSeconds()); }));
		this._laterButton.addEventListener("click", () => this._edit(clip => { clip.start += 0.01 / this._partSeconds(); }));
		this._splitButton.addEventListener("click", this._split);
		this._duplicateButton.addEventListener("click", this._duplicate);
		this._normalizeButton.addEventListener("click", this._normalize);
		this._deleteButton.addEventListener("click", this._delete);
		this._zoomOut.addEventListener("click", () => this._setZoom(this._zoom - 1));
		this._zoomIn.addEventListener("click", () => this._setZoom(this._zoom + 1));
		void this._listInputs();
		setTimeout(() => { this._recordButton.focus(); this._reveal(this._cursor); });
	}

	public cleanUp = (): void => {
		cancelAnimationFrame(this._frame);
		void this._stop(true);
		this._doc.notifier.unwatch(this._whenSongChanged);
	}

	// --- Time and layout -------------------------------------------------

	private _partSeconds(): number {
		return 60 / (this._doc.song.getBeatsPerMinute() * Config.partsPerBeat);
	}
	private _songParts(): number {
		return this._doc.song.barCount * this._doc.song.beatsPerBar * Config.partsPerBeat;
	}
	private _clipEnd(clip: AudioClip): number {
		return clip.start + clip.duration / this._partSeconds();
	}
	private _pxPerPart(): number {
		return Math.min(ZOOMS[this._zoom] / Config.partsPerBeat, MAX_WIDTH / this._songParts());
	}
	private _setZoom(zoom: number): void {
		const center = this._parts(this._scroller.scrollLeft + this._scroller.clientWidth / 2);
		this._zoom = Math.max(0, Math.min(ZOOMS.length - 1, zoom));
		this._zoomOut.disabled = this._zoom == 0;
		this._zoomIn.disabled = this._zoom == ZOOMS.length - 1;
		this._draw();
		this._scroller.scrollLeft = this._x(center) - this._scroller.clientWidth / 2;
	}
	/** Scrolls so a song position is in view. */
	private _reveal(parts: number): void {
		const x = this._x(parts);
		if (x < this._scroller.scrollLeft || x > this._scroller.scrollLeft + this._scroller.clientWidth - 20) this._scroller.scrollLeft = Math.max(0, x - 40);
	}
	private _x(parts: number): number {
		return parts * this._pxPerPart();
	}
	private _parts(x: number): number {
		return Math.max(0, Math.min(this._songParts(), x / this._timeline.width * this._songParts()));
	}
	private _snap(parts: number, fine: boolean): number {
		return fine ? Math.round(parts) : Math.round(parts / Config.partsPerBeat) * Config.partsPerBeat;
	}

	// --- Song state --------------------------------------------------------

	private _whenSongChanged = (): void => {
		if (this._drag == null) this._refresh();
	}

	/** Re-reads the channel's clips (after an edit, undo or redo). */
	private _refresh(): void {
		this._clips = this._doc.song.channels[this._channel].clips.map(clip => clip.clone());
		if (this._selected >= this._clips.length) this._selected = this._clips.length - 1;
		const missing = this._clips.filter(clip => !recordedTakes.has(clip.take)).length;
		if (missing > 0 && this._take == null) {
			this._status.textContent = missing == 1
				? "One clip's audio isn't on this device (it was recorded elsewhere), so it plays silently."
				: missing + " clips' audio isn't on this device (recorded elsewhere), so they play silently.";
		}
		this._updateControls();
		this._draw();
	}

	/** Applies clips to the song right away (playback follows); the whole
	 * session becomes one undoable change when the dialog closes with Done. */
	private _commit(clips: AudioClip[]): void {
		const channel = this._doc.song.channels[this._channel];
		clips = clips.map(clip => clip.quantized());
		if (JSON.stringify(channel.clips) == JSON.stringify(clips)) { this._refresh(); return; }
		this._undo.push(channel.clips.map(clip => clip.clone()));
		this._redo.length = 0;
		this._apply(clips);
	}
	private _apply(clips: AudioClip[]): void {
		this._doc.song.channels[this._channel].clips = clips.map(clip => clip.quantized());
		this._doc.notifier.changed();
		this._refresh();
	}
	private _undoEdit = (): void => {
		const previous = this._undo.pop();
		if (previous == undefined) return;
		this._redo.push(this._doc.song.channels[this._channel].clips.map(clip => clip.clone()));
		this._selected = -1;
		this._apply(previous);
	}
	private _redoEdit = (): void => {
		const next = this._redo.pop();
		if (next == undefined) return;
		this._undo.push(this._doc.song.channels[this._channel].clips.map(clip => clip.clone()));
		this._selected = -1;
		this._apply(next);
	}

	/** Applies an edit to the selected clip as one undoable step. */
	private _edit(change: (clip: AudioClip) => void): void {
		if (this._selected < 0) return;
		const clips = this._clips.map(clip => clip.clone());
		change(clips[this._selected]);
		this._commit(clips);
	}

	private _updateControls(): void {
		const clip = this._clips[this._selected];
		const recording = this._take != null;
		for (const control of [this._gain, this._fadeIn, this._fadeOut, this._splitButton, this._duplicateButton, this._normalizeButton, this._earlierButton, this._laterButton, this._deleteButton]) {
			control.disabled = clip == undefined || recording;
		}
		this._clipControls.classList.toggle("disabled", clip == undefined);
		this._recordButton.disabled = recording;
		this._undoButton.disabled = recording || this._undo.length == 0;
		this._redoButton.disabled = recording || this._redo.length == 0;
		this._playButton.disabled = recording;
		this._stopButton.disabled = !recording && !this._doc.synth.playing;
		if (clip != undefined) {
			// Never overwrite a field the user is typing in.
			const set = (field: HTMLInputElement, value: string) => { if (document.activeElement !== field) field.value = value; };
			set(this._gain, String(clip.gain));
			this._gainValue.textContent = clip.gain + " dB";
			set(this._fadeIn, String(Math.round(clip.fadeIn * 1000)));
			set(this._fadeOut, String(Math.round(clip.fadeOut * 1000)));
			this._fadeIn.max = this._fadeOut.max = String(Math.round(clip.duration * 1000));
			this._splitButton.disabled = recording || !(this._cursor > clip.start && this._cursor < this._clipEnd(clip));
		} else {
			this._gainValue.textContent = "";
		}
	}

	// --- Drawing -------------------------------------------------------------

	private _draw = (): void => {
		const canvasElement = this._timeline;
		const width = Math.max(1, Math.round(this._songParts() * this._pxPerPart()));
		if (canvasElement.width != width) { canvasElement.width = width; canvasElement.style.width = width + "px"; }
		const g = canvasElement.getContext("2d");
		if (g == null) return;
		const song = this._doc.song;
		g.clearRect(0, 0, width, canvasElement.height);
		g.fillStyle = "#11151c";
		g.fillRect(0, 0, width, LANE_HEIGHT);
		// Bars, with a number every few bars.
		const every = Math.max(1, Math.ceil(song.barCount / (width / 40)));
		g.font = "10px sans-serif";
		for (let bar = 0; bar <= song.barCount; bar++) {
			const x = Math.round(this._x(bar * song.beatsPerBar * Config.partsPerBeat)) + 0.5;
			g.strokeStyle = bar % every == 0 ? "#3b4251" : "#262b36";
			g.beginPath(); g.moveTo(x, 0); g.lineTo(x, LANE_HEIGHT); g.stroke();
			if (bar % every == 0 && bar < song.barCount) { g.fillStyle = "#aeb7c6"; g.fillText(String(bar + 1), x + 3, LANE_HEIGHT + 14); }
		}
		this._clips.forEach((clip, index) => this._drawClip(g, clip, index == this._selected));
		const cursor = Math.round(this._x(this._cursor)) + 0.5;
		g.strokeStyle = "#bbb1ff"; g.lineWidth = 1.5;
		g.beginPath(); g.moveTo(cursor, 0); g.lineTo(cursor, LANE_HEIGHT); g.stroke();
		g.lineWidth = 1;
		if (this._doc.synth.playing || this._take != null) {
			const head = this._x(this._doc.synth.playhead * song.beatsPerBar * Config.partsPerBeat);
			if (head > this._scroller.scrollLeft + this._scroller.clientWidth - 40) this._scroller.scrollLeft = head - 40;
			g.fillStyle = "#edf0f5"; g.fillRect(Math.round(head), 0, 2, LANE_HEIGHT);
			if (this._take != null) this._drawLiveTake(g);
			cancelAnimationFrame(this._frame);
			this._frame = requestAnimationFrame(this._draw);
		}
	}

	private _drawClip(g: CanvasRenderingContext2D, clip: AudioClip, selected: boolean): void {
		const x0 = this._x(clip.start), x1 = this._x(this._clipEnd(clip));
		const take = recordedTakes.get(clip.take);
		g.fillStyle = selected ? "#3a3560" : "#2e2b45";
		g.fillRect(x0, 4, Math.max(1, x1 - x0), LANE_HEIGHT - 8);
		if (take != undefined) {
			// Peak per pixel column over the clip's stretch of the take.
			const gain = Math.pow(10, clip.gain / 20), mid = LANE_HEIGHT / 2, half = LANE_HEIGHT / 2 - 8;
			g.fillStyle = selected ? "#d4ceff" : "#bbb1ff";
			const columns = Math.max(1, Math.floor(x1 - x0));
			const perColumn = clip.duration * take.rate / columns;
			for (let c = 0; c < columns; c++) {
				const from = Math.floor(clip.offset * take.rate + c * perColumn), to = Math.min(take.data.length, Math.floor(from + perColumn) + 1);
				let peak = 0;
				for (let i = Math.max(0, from); i < to; i += Math.max(1, Math.floor((to - from) / 64))) peak = Math.max(peak, Math.abs(take.data[i]));
				let envelope = 1;
				const t = c / columns * clip.duration;
				if (clip.fadeIn > 0 && t < clip.fadeIn) envelope = t / clip.fadeIn;
				if (clip.fadeOut > 0 && clip.duration - t < clip.fadeOut) envelope = Math.min(envelope, (clip.duration - t) / clip.fadeOut);
				const h = Math.min(half, peak * gain * envelope * half);
				g.fillRect(x0 + c, mid - h, 1, Math.max(1, h * 2));
			}
		} else {
			g.fillStyle = "#aeb7c6"; g.fillText("audio not on this device", x0 + 4, LANE_HEIGHT / 2 + 3);
		}
		g.strokeStyle = selected ? "#edf0f5" : "#586378";
		g.strokeRect(x0 + 0.5, 4.5, Math.max(1, x1 - x0 - 1), LANE_HEIGHT - 9);
		if (selected) { g.fillStyle = "#edf0f5"; g.fillRect(x0, 4, 3, LANE_HEIGHT - 8); g.fillRect(x1 - 3, 4, 3, LANE_HEIGHT - 8); }
	}

	private _drawLiveTake(g: CanvasRenderingContext2D): void {
		const take = this._take!;
		const ctx = this._doc.synth.audioContext;
		if (ctx == null) return;
		const seconds = take.chunks.reduce((n, chunk) => n + chunk.length, 0) / ctx.sampleRate - take.prerollSeconds;
		if (seconds <= 0) return;
		const x0 = this._x(take.startPart), x1 = this._x(take.startPart + seconds / this._partSeconds());
		g.fillStyle = "rgba(255, 120, 120, 0.25)"; g.fillRect(x0, 4, x1 - x0, LANE_HEIGHT - 8);
	}

	// --- Pointer editing ----------------------------------------------------

	private _hit(x: number): {index: number; mode: Drag["mode"]} | null {
		for (let index = this._clips.length - 1; index >= 0; index--) {
			const clip = this._clips[index];
			const x0 = this._x(clip.start), x1 = this._x(this._clipEnd(clip));
			if (x < x0 - 2 || x > x1 + 2) continue;
			// Narrow clips keep a smaller handle at each edge.
			const handle = Math.min(HANDLE, (x1 - x0) / 4);
			if (x <= x0 + handle) return {index, mode: "start"};
			if (x >= x1 - handle) return {index, mode: "end"};
			return {index, mode: "move"};
		}
		return null;
	}

	private _pointerX(event: PointerEvent): number {
		const rect = this._timeline.getBoundingClientRect();
		return (event.clientX - rect.left) * this._timeline.width / rect.width;
	}

	private _whenPointerDown = (event: PointerEvent): void => {
		if (this._take != null) return;
		// Keyboard edits (Delete, undo) act on what was just clicked.
		this._timeline.focus();
		const x = this._pointerX(event);
		const hit = this._hit(x);
		if (hit == null) {
			this._selected = -1;
			this._cursor = this._snap(this._parts(x), event.shiftKey);
		} else {
			// Clicking a clip selects it and puts the cursor there (for Split).
			this._selected = hit.index;
			if (hit.mode == "move") this._cursor = this._snap(this._parts(x), event.shiftKey);
			this._drag = {mode: hit.mode, index: hit.index, x, original: this._clips[hit.index].clone()};
			this._timeline.setPointerCapture(event.pointerId);
		}
		this._updateControls();
		this._draw();
	}

	private _whenPointerMove = (event: PointerEvent): void => {
		const x = this._pointerX(event);
		if (this._drag == null) {
			const hit = this._hit(x);
			this._timeline.style.cursor = hit == null ? "text" : hit.mode == "move" ? "grab" : "ew-resize";
			return;
		}
		const {original, mode, index} = this._drag;
		const clip = this._clips[index];
		const take = recordedTakes.get(clip.take);
		const takeSeconds = take ? take.data.length / take.rate : original.offset + original.duration;
		const partSeconds = this._partSeconds();
		const delta = this._parts(x) - this._parts(this._drag.x);
		if (mode == "move") {
			clip.start = Math.max(0, this._snap(original.start + delta, event.shiftKey));
		} else if (mode == "start") {
			// The clip's left edge moves; the audio under it stays where it was.
			let start = this._snap(original.start + delta, event.shiftKey);
			start = Math.max(start, original.start - original.offset / partSeconds, 0);
			start = Math.min(start, this._clipEnd(original) - MIN_SECONDS / partSeconds);
			const shift = (start - original.start) * partSeconds;
			clip.start = start;
			clip.offset = original.offset + shift;
			clip.duration = original.duration - shift;
		} else {
			const end = this._snap(this._clipEnd(original) + delta, event.shiftKey);
			clip.duration = Math.max(MIN_SECONDS, Math.min((end - original.start) * partSeconds, takeSeconds - original.offset));
		}
		clip.fadeIn = Math.min(clip.fadeIn, clip.duration);
		clip.fadeOut = Math.min(clip.fadeOut, clip.duration);
		this._draw();
	}

	private _whenPointerUp = (): void => {
		if (this._drag == null) return;
		this._drag = null;
		this._commit(this._clips);
	}

	private _whenKeyDown = (event: KeyboardEvent): void => {
		if ((event.target as HTMLElement).tagName == "INPUT" && (event.target as HTMLInputElement).type == "number") return;
		if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() == "z") {
			if (event.shiftKey) this._redoEdit(); else this._undoEdit();
			event.preventDefault();
		} else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() == "y") {
			this._redoEdit();
			event.preventDefault();
		} else if ((event.key == "Delete" || event.key == "Backspace") && this._selected >= 0) {
			this._delete();
			event.preventDefault();
		} else if (event.key == "Escape") {
			this._cancel();
			event.preventDefault();
		}
		event.stopPropagation();
	}

	// --- Clip operations ----------------------------------------------------

	private _split = (): void => {
		const clip = this._clips[this._selected];
		if (clip == undefined || !(this._cursor > clip.start && this._cursor < this._clipEnd(clip))) return;
		const at = (this._cursor - clip.start) * this._partSeconds();
		const first = clip.clone(), second = clip.clone();
		first.duration = at;
		first.fadeOut = 0;
		first.fadeIn = Math.min(first.fadeIn, at);
		second.start = this._cursor;
		second.offset = clip.offset + at;
		second.duration = clip.duration - at;
		second.fadeIn = 0;
		second.fadeOut = Math.min(second.fadeOut, second.duration);
		const clips = this._clips.slice();
		clips.splice(this._selected, 1, first, second);
		this._selected += 1;
		this._commit(clips);
	}

	private _duplicate = (): void => {
		const clip = this._clips[this._selected];
		if (clip == undefined) return;
		const copy = clip.clone();
		copy.start = this._clipEnd(clip);
		const clips = this._clips.concat([copy]);
		this._selected = clips.length - 1;
		this._commit(clips);
	}

	private _normalize = (): void => {
		const clip = this._clips[this._selected];
		const take = clip && recordedTakes.get(clip.take);
		if (!take) return;
		let peak = 0;
		const end = Math.min(take.data.length, Math.ceil((clip.offset + clip.duration) * take.rate));
		for (let i = Math.floor(clip.offset * take.rate); i < end; i++) peak = Math.max(peak, Math.abs(take.data[i]));
		if (peak <= 0) return;
		// Peaks at -1 dBFS.
		this._edit(target => { target.gain = Math.max(-60, Math.min(12, Math.round(20 * Math.log10(0.891 / peak) * 10) / 10)); });
	}

	private _delete = (): void => {
		if (this._selected < 0) return;
		const clips = this._clips.slice();
		clips.splice(this._selected, 1);
		this._selected = -1;
		this._commit(clips);
	}

	/** Clips with the stretch [from, to) (parts) cut out, for a take that replaces it. */
	private _cut(clips: AudioClip[], from: number, to: number): AudioClip[] {
		const partSeconds = this._partSeconds();
		const out: AudioClip[] = [];
		for (const clip of clips) {
			const end = this._clipEnd(clip);
			if (end <= from || clip.start >= to) { out.push(clip); continue; }
			if (clip.start < from) {
				const before = clip.clone();
				before.duration = (from - clip.start) * partSeconds;
				before.fadeOut = Math.min(before.fadeOut, before.duration);
				out.push(before);
			}
			if (end > to) {
				const after = clip.clone();
				const skip = (to - clip.start) * partSeconds;
				after.start = to;
				after.offset = clip.offset + skip;
				after.duration = clip.duration - skip;
				after.fadeIn = 0;
				out.push(after);
			}
		}
		return out;
	}

	// --- Playback and recording -------------------------------------------

	private async _listInputs(): Promise<void> {
		if (!navigator.mediaDevices?.enumerateDevices) return;
		const devices = await navigator.mediaDevices.enumerateDevices();
		const inputs = devices.filter(device => device.kind == "audioinput" && device.deviceId && device.deviceId != "default");
		for (const device of inputs) this._input.appendChild(option({value: device.deviceId}, device.label || "Microphone " + (this._input.options.length)));
	}

	private _play = (): void => {
		const song = this._doc.song;
		this._doc.synth.playhead = this._cursor / (song.beatsPerBar * Config.partsPerBeat);
		this._doc.synth.play();
		this._updateControls();
		this._draw();
	}

	private async _startRecording(): Promise<void> {
		if (this._take != null) return;
		if (!navigator.mediaDevices?.getUserMedia) {
			this._status.textContent = "This browser can't record audio.";
			return;
		}
		let stream: MediaStream;
		try {
			const deviceId = this._input.value;
			stream = await navigator.mediaDevices.getUserMedia({audio: {
				deviceId: deviceId ? {exact: deviceId} : undefined,
				// A take should sound like the performance, not a phone call.
				echoCancellation: false, noiseSuppression: false, autoGainControl: false,
			}});
		} catch (error) {
			this._status.textContent = "Microphone unavailable: " + ((error as Error).message || "permission denied") + ".";
			return;
		}
		if (this._input.options.length == 1) void this._listInputs();
		const song = this._doc.song, synth = this._doc.synth;
		const partsPerBar = song.beatsPerBar * Config.partsPerBeat;
		const startPart = this._cursor;
		const prerollBars = this._countIn.checked ? Math.min(1, startPart / partsPerBar) : 0;
		const metronome = synth.enableMetronome;
		synth.enableMetronome = this._metronome.checked;
		synth.playhead = startPart / partsPerBar - prerollBars;
		synth.play();
		const ctx = synth.audioContext!;
		const source = ctx.createMediaStreamSource(stream);
		const processor = ctx.createScriptProcessor(4096, 1, 1);
		const silent = ctx.createGain();
		silent.gain.value = 0;
		const take: Take = {stream, source, processor, silent, chunks: [], firstCapture: -1, startPart, prerollSeconds: prerollBars * partsPerBar * this._partSeconds(), playStart: ctx.currentTime, metronome};
		processor.onaudioprocess = (event: AudioProcessingEvent) => {
			if (take.firstCapture < 0) take.firstCapture = ctx.currentTime - event.inputBuffer.duration;
			take.chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
		};
		source.connect(processor);
		processor.connect(silent);
		silent.connect(ctx.destination);
		this._take = take;
		this._selected = -1;
		this._status.textContent = prerollBars > 0 ? "Lead-in… recording starts at the cursor." : "Recording…";
		this._updateControls();
		this._draw();
	}

	private async _stop(discard: boolean = false): Promise<void> {
		const synth = this._doc.synth;
		synth.pause();
		const take = this._take;
		this._take = null;
		if (take == null) {
			this._updateControls();
			this._draw();
			return;
		}
		synth.enableMetronome = take.metronome;
		take.processor.onaudioprocess = null;
		take.source.disconnect();
		take.processor.disconnect();
		take.silent.disconnect();
		for (const track of take.stream.getTracks()) track.stop();
		if (discard) return;
		const ctx = synth.audioContext!;
		const rate = ctx.sampleRate;
		const data = new Float32Array(take.chunks.reduce((n, chunk) => n + chunk.length, 0));
		let at = 0;
		for (const chunk of take.chunks) { data.set(chunk, at); at += chunk.length; }
		// What the performer heard at the start left the speakers after the
		// synth's buffer and the output latency, and their sound reached this
		// recorder after the input latency: skip that, then the lead-in.
		const heard = take.playStart + synth.outputBufferSize / rate + (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
		const inputLatency = ctx.baseLatency || 0;
		const offset = Math.max(0, heard + inputLatency - Math.max(0, take.firstCapture)) + take.prerollSeconds;
		const duration = data.length / rate - offset;
		if (duration < 0.1) {
			this._status.textContent = `That take was too short to keep (${(data.length / rate).toFixed(1)} s captured).`;
			this._updateControls();
			this._draw();
			return;
		}
		const id = newTakeId();
		try {
			await saveTake(id, rate, data);
		} catch {
			this._status.textContent = "Couldn't save the take on this device; it will play until you leave the page.";
		}
		const clip = new AudioClip();
		clip.take = id;
		clip.start = take.startPart;
		clip.offset = offset;
		clip.duration = duration;
		const end = this._clipEnd(clip);
		const clips = (this._replace.checked ? this._cut(this._clips, clip.start, end) : this._clips.slice()).concat([clip]);
		clips.sort((a, b) => a.start - b.start);
		this._selected = clips.indexOf(clip);
		this._status.textContent = `Recorded ${duration.toFixed(1)} s. If it sounds early or late, nudge it by 10 ms.`;
		this._commit(clips);
	}

	/** Keeps the session's edits as one undoable change. */
	private _done = (): void => {
		void this._stop(true);
		const clips = this._doc.song.channels[this._channel].clips.map(clip => clip.clone());
		this._doc.song.channels[this._channel].clips = this._original.map(clip => clip.clone());
		this._doc.prompt = null;
		this._doc.record(new ChangeChannelClips(this._doc, this._channel, clips), true);
	}

	/** Leaves the channel's clips as they were when the dialog opened. */
	private _cancel = (): void => {
		void this._stop(true);
		this._doc.song.channels[this._channel].clips = this._original.map(clip => clip.clone());
		this._doc.notifier.changed();
		this._doc.undo();
	}
}
